/**
 * Payment Links: shareable links units create for charity collections and other payments.
 *
 * Firestore (written only by Cloud Functions):
 *   units/{unitId}/paymentLinks/{linkId}
 *   units/{unitId}/paymentLinks/{linkId}/payments/{paymentId}
 *   units/{unitId}/paymentLinks/{linkId}/activity/{activityId}
 *
 * Amounts are integer pence.
 */

export type PaymentLinkAmountType = 'fixed' | 'range' | 'open';
export type PaymentLinkFeeMode = 'on_top' | 'included';
export type PaymentLinkMethod = 'stripe' | 'sumup' | 'slickpay';
export type PaymentLinkStatus = 'active' | 'disabled' | 'archived';
export type PaymentLinkNotifyMode = 'each' | 'daily' | 'off';
export type PaymentLinkPayableState = 'active' | 'expired' | 'disabled' | 'archived' | 'method_unavailable';
export type PaymentLinkPaymentStatus = 'pending' | 'paid' | 'failed' | 'cancelled';

export const PAYMENT_LINK_METHODS: PaymentLinkMethod[] = ['stripe', 'sumup', 'slickpay'];

export interface PaymentLinkNotifications {
  mode: PaymentLinkNotifyMode;
  extraRecipients: string[];
  expiryReminder: boolean;
}

/** The editable configuration of a link (what the create/edit form sends). */
export interface PaymentLinkConfigInput {
  title: string;
  description: string;
  reference: string;
  thankYouMessage: string;
  amountType: PaymentLinkAmountType;
  fixedAmountPence: number | null;
  minAmountPence: number | null;
  maxAmountPence: number | null;
  suggestedAmountsPence: number[];
  feeMode: PaymentLinkFeeMode;
  paymentMethod: PaymentLinkMethod;
  giftAidEnabled: boolean;
  /** ISO string, or null for no expiry. */
  expiresAt: string | null;
  notifications: PaymentLinkNotifications;
}

export interface PaymentLink {
  id: string;
  unitId: string;
  title: string;
  description: string;
  reference: string;
  thankYouMessage: string;
  amountType: PaymentLinkAmountType;
  fixedAmountPence: number | null;
  minAmountPence: number | null;
  maxAmountPence: number | null;
  suggestedAmountsPence: number[];
  feeMode: PaymentLinkFeeMode;
  paymentMethod: PaymentLinkMethod;
  giftAidEnabled: boolean;
  expiresAt: Date | null;
  status: PaymentLinkStatus;
  notifications: PaymentLinkNotifications;
  targetUrl: string;
  shortUrl: string;
  shortenerUsed: boolean;
  totalCollectedPence: number;
  totalNetPence: number;
  totalFeesPence: number;
  paymentCount: number;
  giftAidCount: number;
  giftAidEligiblePence: number;
  lastPaymentAt: Date | null;
  createdAt: Date | null;
  updatedAt: Date | null;
  createdBy: string;
  createdByName: string;
}

export interface PaymentLinkGiftAid {
  title?: string;
  firstName: string;
  lastName: string;
  houseNameOrNumber: string;
  postcode: string;
  declarationVersion: string;
  declaredAt: Date | null;
  eligiblePence: number;
}

export interface PaymentLinkStatusHistoryEntry {
  status: PaymentLinkPaymentStatus;
  at: Date | null;
  source: 'checkout' | 'webhook' | 'return' | 'sweep' | 'manual';
  note?: string;
}

export interface PaymentLinkPayment {
  id: string;
  unitId: string;
  linkId: string;
  status: PaymentLinkPaymentStatus;
  method: PaymentLinkMethod;
  feeMode: PaymentLinkFeeMode;
  reference: string;
  amountPence: number;
  processingFeePence: number;
  totalChargedPence: number;
  netToUnitPence: number;
  anonymous: boolean;
  payerName: string | null;
  payerEmail: string | null;
  message: string | null;
  giftAid: PaymentLinkGiftAid | null;
  stripeSessionId?: string;
  stripePaymentIntentId?: string;
  sumupCheckoutId?: string;
  sumupTransactionCode?: string;
  slickpayPoId?: string;
  creditsDebited?: number;
  receiptSentAt: Date | null;
  receiptSendCount: number;
  leadsNotifiedAt: Date | null;
  closedReason?: string;
  statusHistory: PaymentLinkStatusHistoryEntry[];
  createdAt: Date | null;
  paidAt: Date | null;
}

export interface PaymentLinkActivity {
  id: string;
  action: string;
  message: string;
  actorName: string | null;
  createdAt: Date | null;
}

/** What getPublicPaymentLink returns to the payer-facing page. */
export interface PublicPaymentLinkResponse {
  state: PaymentLinkPayableState;
  unit: { name: string; logoUrl: string | null };
  link: {
    id: string;
    title: string;
    description: string;
    reference: string;
    thankYouMessage: string;
    amountType: PaymentLinkAmountType;
    fixedAmountPence: number | null;
    minAmountPence: number | null;
    maxAmountPence: number | null;
    suggestedAmountsPence: number[];
    feeMode: PaymentLinkFeeMode;
    feePence: number;
    paymentMethod: PaymentLinkMethod;
    giftAidEnabled: boolean;
    expiresAtMs: number | null;
  };
  giftAid: { template: string; version: string };
}

/** What confirmPaymentLinkPayment returns to the return page. */
export interface PaymentLinkConfirmResponse {
  status: PaymentLinkPaymentStatus;
  amountPence: number;
  processingFeePence: number;
  totalChargedPence: number;
  feeMode: PaymentLinkFeeMode;
  reference: string;
  linkTitle: string;
  thankYouMessage: string;
  unitName: string;
  receiptEmail: string | null;
  giftAid: boolean;
  closedReason: string | null;
}
